import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Text } from "@/components/podkit/typography/Text";
import { Markdown } from "@/components/ai/chat/Markdown";
import type { AgentMessageFragmentCodeAnnotation } from "@/hooks/use-stream-conversation";
import { type FC } from "react";

type FragmentCodeAnnotationProps = {
    fragment: AgentMessageFragmentCodeAnnotation;
    onClick?: (fragment: AgentMessageFragmentCodeAnnotation) => void;
};

export const FragmentCodeAnnotation: FC<FragmentCodeAnnotationProps> = ({ fragment, onClick }) => {
    const getTitle = (): string => {
        return fragment.title || "Code Annotation";
    };

    const lineRange =
        fragment.startLine === fragment.endLine
            ? `Line ${fragment.startLine}`
            : `Lines ${fragment.startLine}-${fragment.endLine}`;

    return (
        <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
            <FragmentCodeAnnotationHeader
                fragment={fragment}
                title={getTitle()}
                lineRange={lineRange}
                onFileClick={() => onClick?.(fragment)}
            />
            <FragmentCodeAnnotationContent fragment={fragment} />
        </div>
    );
};

const FragmentCodeAnnotationHeader: FC<{
    fragment: AgentMessageFragmentCodeAnnotation;
    title: string;
    lineRange: string;
    onFileClick?: () => void;
}> = ({ fragment, title, lineRange, onFileClick }) => {
    const handleFilePathClick = () => {
        if (onFileClick) {
            onFileClick();
        }
    };
    return (
        <div className="flex items-start gap-2 bg-surface-02 p-2">
            <IconInfo size="base" className="mt-0.5 shrink-0 text-content-secondary" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Text className="truncate text-sm font-bold text-content-secondary">{title}</Text>
                <div className="flex items-center justify-between gap-2">
                    <button
                        className="min-w-0 cursor-pointer truncate border-none bg-transparent p-0 text-left text-sm font-bold text-content-secondary hover:text-content-link hover:underline"
                        onClick={handleFilePathClick}
                        data-tracking-id="click-code-annotation-file-path"
                    >
                        {fragment.filePath}
                    </button>
                    <Text className="shrink-0 text-xs text-content-tertiary">{lineRange}</Text>
                </div>
            </div>
        </div>
    );
};

const FragmentCodeAnnotationContent: FC<{
    fragment: AgentMessageFragmentCodeAnnotation;
}> = ({ fragment }) => {
    return (
        <ScrollArea orientation="both" className="flex max-h-[200px] border-t border-border-base p-0">
            <div className="p-3">
                <Markdown content={fragment.description} className="text-content-primary" />
            </div>
        </ScrollArea>
    );
};
